import { useEffect, useState } from "react";
import DashboardLayout from "../layouts/DashboardLayout";
import { Card, CardContent } from "../components/ui/CCard";
import ManualReviewBadge from "../components/ui/ManualReviewBadge";
import ConfidenceIndicator from "../components/ui/ConfidenceIndicator";
import { Link } from "react-router-dom";
import api from "../api";

export default function ManualReviewDashboard() {
    const [sessions, setSessions] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [priorityFilter, setPriorityFilter] = useState(''); // Default: show all (change to 'critical' or 'high' to filter by default)

    // Modal State
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [activeSessionId, setActiveSessionId] = useState(null);
    const [reviewComment, setReviewComment] = useState("");

    useEffect(() => {
        fetchData();
    }, [priorityFilter]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch stats
            const statsRes = await api.getManualReviewStats();
            setStats(statsRes.data);

            // Fetch flagged sessions
            const sessionsRes = await api.getFlaggedSessions(priorityFilter || null);
            setSessions(sessionsRes.data.sessions || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const openReviewModal = (sessionId) => {
        setActiveSessionId(sessionId);
        setReviewComment("");
        setShowReviewModal(true);
    };

    useEffect(() => {
        const handleEsc = (event) => {
            if (event.keyCode === 27) setShowReviewModal(false);
        };
        if (showReviewModal) {
            window.addEventListener('keydown', handleEsc);
        }
        return () => {
            window.removeEventListener('keydown', handleEsc);
        };
    }, [showReviewModal]);

    const submitReview = async () => {
        if (!activeSessionId) return;

        if (!reviewComment.trim()) {
            alert("Review comment is required.");
            return;
        }

        try {
            await api.markSessionAsReviewed(activeSessionId, undefined, reviewComment);
            setSessions(sessions.filter(s => s.id !== activeSessionId));
            fetchData(); // Refresh stats

            // Close modal
            setShowReviewModal(false);
            setActiveSessionId(null);
            setReviewComment("");

            // Optional: Show success toast/alert
            // alert('Session marked as reviewed successfully!');
        } catch (err) {
            alert(`Error: ${err.response?.data?.error || err.message}`);
        }
    };

    return (
        <DashboardLayout>
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-2xl sm:text-3xl font-heading font-bold text-secondary-900">
                    Manual Review Dashboard
                </h1>
                <p className="text-secondary-500 mt-1">
                    Manage sessions flagged for expert review
                </p>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                        <CardContent className="p-6">
                            <div className="text-3xl font-bold text-red-700">{stats.by_priority.critical}</div>
                            <div className="text-sm text-red-600 font-medium mt-1">Critical Priority</div>
                        </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                        <CardContent className="p-6">
                            <div className="text-3xl font-bold text-orange-700">{stats.by_priority.high}</div>
                            <div className="text-sm text-orange-600 font-medium mt-1">High Priority</div>
                        </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
                        <CardContent className="p-6">
                            <div className="text-3xl font-bold text-yellow-700">{stats.by_priority.medium}</div>
                            <div className="text-sm text-yellow-600 font-medium mt-1">Medium Priority</div>
                        </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                        <CardContent className="p-6">
                            <div className="text-3xl font-bold text-blue-700">{stats.by_priority.low}</div>
                            <div className="text-sm text-blue-600 font-medium mt-1">Low Priority</div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Filters */}
            <Card className="mb-6">
                <CardContent className="p-6">
                    <div className="flex flex-wrap items-center gap-4">
                        <label className="text-sm font-semibold text-secondary-700">Filter by Priority:</label>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPriorityFilter('')}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${priorityFilter === ''
                                    ? 'bg-primary-500 text white shadow-md'
                                    : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                                    }`}
                            >
                                All
                            </button>
                            <button
                                onClick={() => setPriorityFilter('critical')}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${priorityFilter === 'critical'
                                    ? 'bg-red-500 text-white shadow-md'
                                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                                    }`}
                            >
                                Critical
                            </button>
                            <button
                                onClick={() => setPriorityFilter('high')}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${priorityFilter === 'high'
                                    ? 'bg-orange-500 text-white shadow-md'
                                    : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                                    }`}
                            >
                                High
                            </button>
                            <button
                                onClick={() => setPriorityFilter('medium')}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${priorityFilter === 'medium'
                                    ? 'bg-yellow-500 text-white shadow-md'
                                    : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                    }`}
                            >
                                Medium
                            </button>
                            <button
                                onClick={() => setPriorityFilter('low')}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${priorityFilter === 'low'
                                    ? 'bg-blue-500 text-white shadow-md'
                                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                    }`}
                            >
                                Low
                            </button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Sessions List */}
            {loading ? (
                <Card className="p-12 text-center">
                    <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p className="text-secondary-500 font-medium">Loading flagged sessions...</p>
                </Card>
            ) : error ? (
                <Card className="p-8 border-red-200 bg-red-50/50">
                    <p className="text-red-500 font-medium text-center">Error: {error}</p>
                </Card>
            ) : sessions.length === 0 ? (
                <Card className="p-12 text-center text-secondary-500">
                    <div className="text-6xl mb-4">✅</div>
                    <p className="text-lg font-medium">No sessions need review</p>
                    <p className="text-sm mt-2">
                        {priorityFilter
                            ? `No ${priorityFilter} priority sessions found`
                            : 'All evaluations are confident and reliable'
                        }
                    </p>
                </Card>
            ) : (
                <div className="space-y-4">
                    {sessions.map((session) => (
                        <Card key={session.id} className="hover:shadow-lg transition-shadow">
                            <CardContent className="p-6">
                                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                                    {/* Left: Session Info */}
                                    <div className="flex-1">
                                        <div className="flex items-start gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <h3 className="text-lg font-semibold text-secondary-900">
                                                        Session #{session.id}
                                                    </h3>
                                                    <ManualReviewBadge
                                                        meta={session.review_meta}
                                                        size="sm"
                                                        showReasons={false}
                                                    />
                                                </div>

                                                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm mb-3">
                                                    <div>
                                                        <span className="text-secondary-500">Patient:</span>{' '}
                                                        <span className="font-medium text-secondary-800">
                                                            {session.patient_name || 'Unknown'}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className="text-secondary-500">Doctor:</span>{' '}
                                                        <span className="font-medium text-secondary-800">
                                                            {session.doctor_name || 'Unknown'}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className="text-secondary-500">Date:</span>{' '}
                                                        <span className="font-medium text-secondary-800">
                                                            {session.sessiondate ? new Date(session.sessiondate).toLocaleDateString() : '-'}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className="text-secondary-500">Flagged:</span>{' '}
                                                        <span className="font-medium text-secondary-800">
                                                            {session.review_meta.flagged_at
                                                                ? new Date(session.review_meta.flagged_at).toLocaleDateString()
                                                                : '-'
                                                            }
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Confidence Indicator */}
                                                <div className="mb-3">
                                                    <ConfidenceIndicator
                                                        confidenceScore={session.review_meta.confidence_score}
                                                        confidenceLevel={session.review_meta.confidence_level}
                                                        size="sm"
                                                        showLabel={true}
                                                    />
                                                </div>

                                                {/* Review Reasons */}
                                                {session.review_meta.review_reasons && session.review_meta.review_reasons.length > 0 && (
                                                    <div className="bg-secondary-50 p-3 rounded-lg border border-secondary-200">
                                                        <p className="text-xs font-semibold text-secondary-700 mb-1.5">Review Reasons:</p>
                                                        <ul className="space-y-1">
                                                            {session.review_meta.review_reasons.map((reason, idx) => (
                                                                <li key={idx} className="text-xs text-secondary-600 flex items-start gap-1.5">
                                                                    <span className="mt-0.5">•</span>
                                                                    <span>{reason}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right: Actions */}
                                    <div className="flex flex-col gap-2">
                                        <Link
                                            to={`/consultation/${session.id}`}
                                            className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-semibold text-center text-sm shadow-md"
                                        >
                                            View Session
                                        </Link>
                                        <button
                                            onClick={() => openReviewModal(session.id)}
                                            className="px-6 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors font-medium text-center text-sm border border-green-300"
                                        >
                                            ✓ Mark as Reviewed
                                        </button>
                                        {/* {session.self_rating !== null && (
                                            <div className="text-center text-xs text-secondary-500">
                                                Self-Rating: {session.self_rating}/10
                                            </div>
                                        )} */}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Stats Footer */}
            {stats && !loading && (
                <Card className="mt-8">
                    <CardContent className="p-6">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
                            <div>
                                <div className="text-2xl font-bold text-secondary-900">{stats.total_flagged}</div>
                                <div className="text-sm text-secondary-500">Total Flagged</div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-secondary-900">{stats.total_evaluated}</div>
                                <div className="text-sm text-secondary-500">Total Evaluated</div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-secondary-900">
                                    {Math.round(stats.avg_confidence * 100)}%
                                </div>
                                <div className="text-sm text-secondary-500">Avg Confidence</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}
            {/* Review Comment Modal */}
            {showReviewModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="p-6 border-b border-secondary-100">
                            <h3 className="text-lg font-bold text-secondary-900">
                                Mark Session as Reviewed
                            </h3>
                            <p className="text-sm text-secondary-500 mt-1">
                                Add a comment or observation about this session.
                            </p>
                        </div>

                        <div className="p-6">
                            <label className="block text-sm font-medium text-secondary-700 mb-2">
                                Review Comment <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                value={reviewComment}
                                onChange={(e) => setReviewComment(e.target.value)}
                                placeholder="e.g., Checked for safety flags, looks good..."
                                className="w-full h-32 px-4 py-3 rounded-lg border border-secondary-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition-all resize-none text-sm"
                                autoFocus
                            />
                        </div>

                        <div className="p-6 bg-secondary-50 border-t border-secondary-100 flex justify-end gap-3">
                            <button
                                onClick={() => setShowReviewModal(false)}
                                className="px-4 py-2 rounded-lg text-sm font-medium text-secondary-600 hover:text-secondary-900 hover:bg-secondary-200 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={submitReview}
                                className="px-4 py-2 rounded-lg text-sm font-medium bg-green-600 text-white hover:bg-green-700 shadow-sm transition-colors flex items-center gap-2"
                            >
                                <span>✓ Confirm Reviewed</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
}
